import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, FileText, Calendar, DollarSign } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://suddyflow-api-cugygabvhfg6dvfj.eastus2-01.azurewebsites.net';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const token = searchParams.get('t');

  const [verifying, setVerifying] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setVerifying(false);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pay/verify/${sessionId}`, { params: { token } });
      setPaymentInfo(response.data);
    } catch (err) {
      console.error('Error verifying payment:', err);
      // Don't show error - payment likely succeeded, just couldn't verify
      setPaymentInfo({ success: true });
    } finally {
      setVerifying(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-6">
          Thank you for your payment. Your payment has been received.
        </p>

        {/* Payment Details */}
        {paymentInfo && (
          <div className="bg-gray-50 rounded-lg p-4 text-left space-y-3 mb-6">
            {paymentInfo.businessName && (
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Paid To</p>
                  <p className="font-medium text-gray-900">{paymentInfo.businessName}</p>
                </div>
              </div>
            )}
            
            {paymentInfo.invoiceNumber && (
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Invoice</p>
                  <p className="font-medium text-gray-900">{paymentInfo.invoiceNumber}</p>
                </div>
              </div>
            )}

            {paymentInfo.amountPaid && (
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Amount Paid</p>
                  <p className="font-medium text-green-600">{formatCurrency(paymentInfo.amountPaid)}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Payment Date</p>
                <p className="font-medium text-gray-900">{formatDate(paymentInfo.paymentDate)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Close Message */}
        <p className="text-sm text-gray-500">
          You can safely close this window.
        </p>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Powered by <span className="font-medium text-gray-700">SuddyFlow</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;